/**
 * ageConsentController — the tiny pub/sub bridge between the AXIOS response
 * interceptor (plain JS, no React) and the React `<AgeConsentGate>` overlay.
 *
 * WHY A CONTROLLER (not React context in the interceptor): the interceptor runs
 * OUTSIDE React's tree — it can't call a hook. But it needs to (a) pop a DOB
 * modal and AWAIT the user's entry before retrying the original request, and
 * (b) raise the under-13 limited-mode overlay. This module is the seam: the
 * interceptor calls `requestDob()` / `showPlatformConsent()`; the mounted gate
 * component `subscribe()`s and drives the actual UI, resolving the promise the
 * interceptor awaits.
 *
 * SINGLE-FLIGHT DOB: many gated requests can 426 at once (a page firing several
 * fetches). We must show ONE modal and, when the user submits their DOB,
 * resolve EVERY pending 426 so they all retry — not stack N modals. So
 * `requestDob()` returns the SAME in-flight promise while a capture is open;
 * the first caller opens the modal, the rest piggyback on its resolution.
 *
 * NO PII RETENTION: the resolved value is a bare `{ ok: true }` signal, never
 * the DOB itself — the controller never holds the date (the modal POSTs it to
 * /auth/age and discards it). The interceptor only needs "DOB is now on file,
 * retry"; it re-issues the original request, which the backend re-gates.
 *
 * NO MODAL, NO CAPTURE: a capture stays open only while the gate whose modal
 * asks it is mounted. The gate lives inside the signed-in tree, and signing
 * out is SPA state only (App.js handleLogout, which the 401 interceptor also
 * calls), so it can unmount with its modal open. Nothing could answer that
 * capture any more: every request parked on it waited for good, and every
 * later 426 in the tab joined it and opened no modal, so the next student to
 * sign in was stuck. So the asking gate's unsubscribe fails the capture (see
 * subscribe()), with a reason of its own (isAgeGateUnmounted()).
 */

// The single subscriber (the mounted gate). One overlay at a time — a second
// subscribe replaces the first (e.g. a hot-reload remount), so we never leak a
// stale listener that would drive an unmounted tree.
let listener = null;

// Single-flight DOB capture state: while a capture modal is open, ALL 426s
// share this one promise + resolver, so a single DOB entry releases them all.
let dobPromise = null;
let dobResolve = null;
let dobReject = null;
// The subscriber whose modal is asking the open capture: the one listening
// when requestDob() opened it. Not simply the current listener: a gate that
// subscribed since then replaced the listener but was never asked (a second
// 426 joins the open capture and opens no modal), while the gate it replaced
// may still be showing the question.
let dobOwner = null;

// The reason a capture fails with when the gate asking it unmounts, so the
// interceptor can tell it from requestDob()'s 'age-gate-unavailable': no gate
// mounted when the 426 arrived, during onboarding or after a logout. (After a
// logout that 426 still reaches a page that is gone, until the session-epoch
// fix, brief 039.)
const GATE_UNMOUNTED = 'age-gate-unmounted';

/**
 * Register the UI subscriber. Returns an unsubscribe fn (called on unmount).
 * `handlers` is `{ onRequestDob(capture), onShowPlatformConsent() }` — the
 * gate wires its own state to these. onRequestDob is handed the capture it is
 * asked to show: `capture.resolve()` / `capture.reject(reason)` settle THAT
 * capture and nothing else (captureFor() below). There is no "hide" signal:
 * the gate closes each overlay itself, and closes the DOB modal only by
 * settling its capture.
 *
 * Unsubscribing while this subscriber's modal is asking an open capture fails
 * that capture (NO MODAL, NO CAPTURE above), whether or not a later gate has
 * replaced it as the listener; a capture another gate is asking is left to
 * that gate. With nothing open it settles nothing, so StrictMode's
 * mount/cleanup/mount replay is unaffected.
 */
export function subscribe(handlers) {
  listener = handlers;
  return () => {
    if (listener === handlers) listener = null;
    if (dobOwner === handlers) rejectDob(new Error(GATE_UNMOUNTED));
  };
}

/**
 * True for the reason a capture fails with when the gate asking it unmounts
 * mid-question. The page that was waiting on it unmounted with the gate (the
 * whole signed-in tree goes at logout), so the interceptor cancels the held
 * request rather than handing that page its 426.
 */
export function isAgeGateUnmounted(reason) {
  return reason instanceof Error && reason.message === GATE_UNMOUNTED;
}

/**
 * Open the DOB-capture modal and resolve when the user has set their DOB
 * (the modal POSTs /auth/age, then settles the capture it was handed).
 * SINGLE-FLIGHT: a second concurrent 426 gets the same promise, so one entry
 * releases every waiter. If no gate is mounted (onboarding; after a logout),
 * reject so the interceptor falls through to surfacing the raw 426 rather
 * than hanging.
 */
export function requestDob() {
  if (dobPromise) return dobPromise;
  // Hold a LOCAL ref: the no-subscriber branch below rejects synchronously,
  // which nulls `dobPromise` — so we must return the captured promise, not the
  // (now-null) module field. Returning null there would orphan the rejected
  // promise (unhandled rejection) AND make the interceptor's `await onAge()`
  // resolve to undefined, wrongly retrying as if a DOB had been captured.
  const p = new Promise((resolve, reject) => {
    dobResolve = resolve;
    dobReject = reject;
  });
  dobPromise = p;
  if (listener?.onRequestDob) {
    dobOwner = listener;
    listener.onRequestDob(captureFor(p));
  } else {
    // No UI to capture with — fail the wait so the original request's 426
    // propagates to the caller instead of hanging forever.
    rejectDob(new Error('age-gate-unavailable'));
  }
  return p;
}

/**
 * The capture onRequestDob() hands its gate: the two ways the modal ends it,
 * bound to this one capture by identity. Once it has ended (settled, or failed
 * because its gate unmounted) both do nothing. A modal callback can outlive
 * its capture: the save is async, so it can land after the gate unmounted
 * mid-save, or after a later capture opened in its place, even in the same
 * gate. Then it must settle nothing: never the next student's capture.
 */
function captureFor(promise) {
  const isOpen = () => dobPromise === promise;
  return {
    resolve() {
      if (isOpen()) resolveDob();
    },
    reject(reason) {
      if (isOpen()) rejectDob(reason);
    },
  };
}

/** Forget the open capture. A caller settling it takes its function first. */
function closeCapture() {
  dobPromise = null;
  dobResolve = null;
  dobReject = null;
  dobOwner = null;
}

/**
 * Resolve the OPEN capture, whichever it is: what a successful /auth/age (or a
 * 409 already-set) does, through the capture the gate was handed. Tests drive
 * the open capture with this and rejectDob(); a gate never calls either
 * directly: its modal's save can land after that capture ended, and they
 * would then settle someone else's.
 */
export function resolveDob() {
  const r = dobResolve;
  closeCapture();
  if (r) r({ ok: true });
}

/**
 * Fail the OPEN capture, whichever it is: the user DISMISSED it without
 * setting a DOB (through the capture the gate was handed), its gate unmounted,
 * or there was no gate. Every pending 426 then rejects → its original caller
 * sees the error and can render its own state, rather than hanging.
 */
export function rejectDob(reason) {
  const rej = dobReject;
  closeCapture();
  if (rej) rej(reason || new Error('age-capture-dismissed'));
}

/** Whether a DOB capture is currently in flight (for tests / guards). */
export function isCapturingDob() {
  return dobPromise !== null;
}

/**
 * Raise the under-13 limited-mode / parent-invite overlay. Fire-and-forget
 * (no promise to await — the under-13 can keep navigating; the overlay is a
 * guiding affordance, not a blocking gate). Idempotent: re-raising while it's
 * open is a no-op at the UI layer.
 */
export function showPlatformConsent() {
  if (listener?.onShowPlatformConsent) listener.onShowPlatformConsent();
}

/**
 * Test-only reset of module singletons between cases (the controller is a
 * module-level singleton; without this a rejected promise from one test could
 * bleed into the next). Not used in production.
 */
export function __resetForTests() {
  listener = null;
  closeCapture();
}
